import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

export type OrderStatus =
  | 'PENDING'
  | 'CONFIRMED'
  | 'READY_FOR_PICKUP'
  | 'SHIPPED'
  | 'DELIVERED'
  | 'PICKED_UP'
  | 'CANCELLED'
  | 'REFUNDED';

export type PaymentStatus =
  | 'PENDING'
  | 'REQUIRES_ACTION'
  | 'PAID'
  | 'FAILED'
  | 'REFUNDED'
  | 'PARTIALLY_REFUNDED';

export type FulfillmentMethod = 'SHIPPING' | 'IN_STORE_PICKUP';

export interface OrderItemDto {
  productId: string;
  productName: { it?: string; en?: string };
  size: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  // T-40/T-137 — personalizzazione maglia (snapshot al momento dell'ordine).
  // Il backend (order-response.dto) restituisce SEMPRE questi campi; null = nessuna
  // personalizzazione. `customNumber` può valere 0 (numero valido 0–99), quindi
  // ovunque va testato con `!= null`, mai con la falsy-ness. `customizationFee` è
  // il sovrapprezzo per singola unità già moltiplicato per i caratteri (0 se assente).
  customName: string | null;
  customNumber: number | null;
  customizationFee: number;
}

export interface AddressSummaryDto {
  id: string;
  label: string;
  recipient: string;
  line1: string;
  line2: string | null;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  phone: string;
}

export interface CustomerSummaryDto {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

export interface OrderDto {
  id: string;
  status: OrderStatus;
  fulfillmentMethod: FulfillmentMethod;
  paymentStatus: PaymentStatus;
  customer: CustomerSummaryDto | null;
  items: OrderItemDto[];
  subtotal: number;
  totalAmount: number;
  discountAmount: number;
  shippingCost: number;
  vatAmount: number;
  refundedAmount: number;
  finalAmount: number;
  currency: string;
  pickupQrCode: string;
  paymentMethod: string;
  paymentIntentId: string | null;
  stripeChargeId: string | null;
  shippingAddress: AddressSummaryDto | null;
  paidAt: string | null;
  courierName: string | null;
  trackingNumber: string | null;
  shippedAt: string | null;
  notes: string | null;
  // T-234 — recapito telefonico del tifoso (E.164). null per gli ordini che non
  // l'hanno (storici o dall'app 1.0.9 che non lo invia).
  contactPhone: string | null;
  couponId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaginatedOrders {
  data: OrderDto[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

interface OrdersFilters {
  page?: number;
  limit?: number;
  status?: OrderStatus;
  paymentStatus?: PaymentStatus;
  fulfillmentMethod?: FulfillmentMethod;
  search?: string;
  from?: string;
  to?: string;
}

const QK = {
  list: (filters: OrdersFilters) => ['orders', filters] as const,
};

export function useOrders(filters: OrdersFilters = {}) {
  return useQuery({
    queryKey: QK.list(filters),
    queryFn: async () => {
      const params: Record<string, string | number> = {};
      if (filters.page) params.page = filters.page;
      if (filters.limit) params.limit = filters.limit;
      if (filters.status) params.status = filters.status;
      if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
      if (filters.fulfillmentMethod) params.fulfillmentMethod = filters.fulfillmentMethod;
      if (filters.search) params.search = filters.search;
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      const res = await apiClient.get<PaginatedOrders>('/shop/orders', { params });
      return res.data;
    },
  });
}

export interface OrdersStats {
  total: number;
  byStatus: Record<OrderStatus, number>;
  confirmedShipping: number;
  paymentPending: number;
  revenue: { netCollected: number; refunded: number; currency: string };
  last24h: { count: number; netCollected: number };
}

/** T-119 — KPI aggregati per la mini-dashboard Ordini. */
export function useOrdersStats() {
  return useQuery({
    queryKey: ['orders', 'stats'] as const,
    queryFn: async () => {
      const res = await apiClient.get<OrdersStats>('/shop/orders/stats');
      return res.data;
    },
  });
}

export interface UpdateOrderPatch {
  id: string;
  status?: OrderStatus;
  courierName?: string;
  trackingNumber?: string;
  // T-234 — correzione del recapito telefonico dell'ordine dal BO.
  contactPhone?: string;
}

export function useUpdateOrderStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status, courierName, trackingNumber, contactPhone }: UpdateOrderPatch) => {
      const body: Record<string, string> = {};
      if (status) body.status = status;
      if (courierName != null) body.courierName = courierName;
      if (trackingNumber != null) body.trackingNumber = trackingNumber;
      if (contactPhone != null) body.contactPhone = contactPhone;
      const res = await apiClient.patch<OrderDto>(`/shop/orders/${id}`, body);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}

export function useDeleteOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/shop/orders/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}

export interface RefundDto {
  amount?: number;
  reason: string;
  restock?: boolean;
}

export function useRefundOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: RefundDto }) => {
      const res = await apiClient.post<{ refundId: string; amountCents: number }>(
        `/payments/${id}/refund`,
        dto,
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
  });
}
